<template>
  <zm-dialog
    id="bp-zoom-one-edu-plan-detail"
    append-to-body
    :visible="visible"
    :aria-label="planName"
    :show-close="showClose"
    @close="onClose"
  >
    <template #title>
      <slot name="back" />
    </template>
    <div v-loading="loading" class="bp-zone-edu-detail-container">
      <h2 class="bp-zone-edu-detail-container__plan-title">{{ planName }}</h2>
      <div class="bp-zone-edu-detail-price">
        <div>
          <div class="bp-zone-edu-price">
            <span class="bp-zone-edu-price__decimal">{{ currencySymbol }}</span>
            <span class="bp-zone-edu-price__integer">{{ monthlyPrice.integer }}</span>
            <span class="bp-zone-edu-price__decimal">{{ monthlyPrice.decimal }}</span>
            <div class="bp-zone-edu-price__unit">
              <span>{{ t('per_billing_cycle_user.monthly') }}</span>
              <price-vat-tip :show-v-a-t-tip="showVATTip" :current-country="currentCountry" />
            </div>
          </div>
        </div>
        <div class="bp-zone-edu-detail-price__divider"></div>
        <div>
          <div class="bp-zone-edu-price">
            <span class="bp-zone-edu-price__decimal">{{ currencySymbol }}</span>
            <span class="bp-zone-edu-price__integer">{{ yearlyPrice.integer }}</span>
            <span class="bp-zone-edu-price__decimal">{{ yearlyPrice.decimal }}</span>
            <div class="bp-zone-edu-price__unit">
              <span>{{ t('per_billing_cycle_user.annually') }}</span>
              <span><price-vat-tip :show-v-a-t-tip="showVATTip" :current-country="currentCountry" /></span>
            </div>
          </div>
        </div>
      </div>
      <div class="bp-zone-edu-detail-items">
        <div v-for="item in detailItems.filter(ele => !ele.hide)" :key="item.title" class="bp-zone-edu-detail-item-part">
          <template>
            <img class="zone-edu-check" src="../../assets/check-item.svg"  alt=""/>
            <div class="bp-zone-edu-detail-content">
              <auto-expire-badge :badgeId="item.badgeId" :theme="'comparison'" style="margin-left: 4px;"/>
              <span class="bp-zone-edu-detail-content__title">{{ item.title }}</span>
              <span v-if="item.desc" class="bp-zone-edu-detail-content__desc">
              {{ firstLetterToLowerCase(item.desc) }}
            </span>
              <div class="fbc-inline-block-item">
                <zm-tooltip v-if="item.tooltip"
                            :content="item.tooltip"
                            :aria-label="item.ariaLabel">
                  <i class="zm-icon-info-outline" style="color: #0b5cff" @mouseenter="tooltipHoverCallback(item.title)"></i>
                </zm-tooltip>
              </div>
              <img v-if="item.iconPath" class="bp-zone-edu-detail-content__icon" :src="getImage(item.iconPath).icon"  alt=""/>
              <div v-if="item.tips && item.tips.show" class="bp-zone-edu-detail-content__tip">
                <span data-testid="common-span-79" @click="item.tips.callback">{{ item.tips.label }}</span>
                <zm-tooltip v-if="item.tips.tooltip" :content="item.tips.tooltip">
                  <i :class="item.tips.iconClass" class="bp-zone-edu-detail-content__icon" @mouseenter="tooltipHoverCallback(item.tips.label)"></i>
                </zm-tooltip>
              </div>
              <div class="bp-zone-edu-detail-content__feature-list">
                <ul v-if="item.featureList">
                  <li v-for="(i, index) in item.featureList" :key="index" class="bp-zone-edu-detail-content__desc">{{ t(i) }}</li>
                </ul>
                <zm-button data-testid="common-button-80" v-if="item.learnMore" type="link" @click="item.learnMore.callback">{{ t('common.learnMore') }}</zm-button>
              </div>
            </div>
          </template>
        </div>
      </div>
      <zm-button data-testid="common-button-81" v-if="fullListCallBack" type="link" @click="fullListCallBack" class="bp-zone-edu-detail-items__feature-list">
        {{ t('common.dialog.feature_list') }}
      </zm-button>
      <slot name="footer"></slot>
    </div>
  </zm-dialog>
</template>
<script>
import { getPricingMap } from '../../datas/pricingMap';
import {
  BILLING_CYCLE,
  RECORDING_CAPACITY,
  ZOOM_ONE_EDU_PLAN_DETAIL_PAGE_ID,
  zoomOneEduSchoolPlanDetailsItemsGetter, zoomOneEduSchoolPlusPlanDetailsItemsGetter,
} from '../../datas/ZoomOneEdu';
import { t } from '../../plugins/i18n'
import { isArray, isEmpty } from 'lodash';
import { pick } from 'lodash/object';
import AutoExpireBadge from '@/components/AUTO_EXPIRE_BADGE';
import priceVatTip from '@/components/PRICE_VAT_TIP';

export default {
  name: 'ZoomOneEduPlanDetailsDialog',
  components: {
    AutoExpireBadge,
    priceVatTip
  },
  props: {
    showClose: {
      type: Boolean,
      default: true
    },
    // distinguish witch plan
    id: {
      type: String,
      default: ZOOM_ONE_EDU_PLAN_DETAIL_PAGE_ID.SCHOOL
    },
    visible: {
      type: Boolean,
      default: false
    },
    fullListCallBack: Function,
    // find price by bizCategoryCode in pricing map
    bizCategoryCode: {
      type: String,
      default: ''
    },
    // large meeting buy
    largeMeetingCallback: Function,
    tooltipHoverCallback: {
      type: Function,
      default () {
        return () => {}
      }
    }, // for event tracking
    currencySymbol: {
      type: String,
      default: ''
    },
    enableDocsFeature: {
      type: Boolean,
      default: false
    },
    enableLargeMeeting3k5k: {
      type: Boolean,
      default: false
    },
    enableDisplayZoomTasksWithoutAccountCheck: {
      type: Boolean,
      default: false
    },
    enableDisplayZoomWorkFlow: {
      type: Boolean,
      default: false
    },
    enableDisplayZoomDrive: {
      type: Boolean,
      default: false
    },
    showCampusScheduler: {
      type: Boolean,
      default: false
    },
    isEligibleAccount4CustomAvatarFeature: {
      type: Boolean,
      default: false,
    },
    enableEduWorkspaceReservations: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      pricingPlansMap: {},
      planName: '',
      detailItems: [],
      currentCountry: window.newBillingConfig?.billingCountry?.soldTo || window.dataModel?.billingCountry?.soldTo || window.newBillingConfig?.currentCountry || window.dataModel?.currentCountry,
      showVATTip: true
    }
  },
  watch: {
    visible(v) {
      v && this.init()
    },
    pricingPlansMap: {
      immediate: true,
      handler(v) {
        if (v && !isEmpty(v)) {
          this.loading = false
        }
      }
    }
  },
  computed: {
    formattedPrice() {
      let priceMapValue
      if (this.id === ZOOM_ONE_EDU_PLAN_DETAIL_PAGE_ID.SCHOOL_PLUS) {
        const zoomone = this.pricingPlansMap['zoomone']
        if (zoomone) {
          priceMapValue = zoomone['zoom-one-edu-sch-cmp-plus']
        }
      }
      if (this.id === ZOOM_ONE_EDU_PLAN_DETAIL_PAGE_ID.SCHOOL) {
        priceMapValue = this.pricingPlansMap['zo_edu_sch_cmp']
      }
      const bizCategoryCode = this.bizCategoryCode
      if (isArray(priceMapValue)) {
        let monthPrice
        let yearPrice
        priceMapValue.forEach(e => {
          if (e.category_code === bizCategoryCode.toLowerCase()) {
            const cycle = e.billing_cycle
            if (cycle === BILLING_CYCLE.MONTHLY) {
              monthPrice = e.amount
            } else if (cycle === BILLING_CYCLE.ANNUAL) {
              yearPrice = e.amount
            }
          }
        })
        return { monthPrice: monthPrice ?? 0, yearPrice: yearPrice?? 0}
      }
      const monthPrice = priceMapValue?.month_amount || 0
      const yearPrice = priceMapValue?.yearly_amount || 0
      return { monthPrice, yearPrice }
    },
    monthlyPrice() {
      const { monthPrice } = this.formattedPrice
      return this.getFixedPrice(monthPrice)
    },
    yearlyPrice() {
      const { yearPrice } = this.formattedPrice
      return this.getFixedPrice(yearPrice)
    }
  },
  methods: {
    t,
    getImage(name) {
      return {
        icon: require(`@/assets/${name}.svg`),
      };
    },
    firstLetterToLowerCase(text) {
      return text.charAt(0).toLowerCase() + text.slice(1)
    },
    getFixedPrice(priceInCents) {
      const price = (priceInCents / 100).toFixed(2)
      const priceArray = price.split('.')
      return {
        integer: priceArray[0],
        decimal: `${priceArray[1] ? `.${priceArray[1]}` : '.00'}`
      }
    },
    async init() {
      this.pricingPlansMap = await getPricingMap()
      const id = this.id
      switch (id) {
        case ZOOM_ONE_EDU_PLAN_DETAIL_PAGE_ID.SCHOOL:
          this.handleSchool()
          break
        case ZOOM_ONE_EDU_PLAN_DETAIL_PAGE_ID.SCHOOL_PLUS:
          this.handleSchoolPlus()
          break
      }
    },
    onClose() {
      this.$emit('closed');
    },
    handleSchool() {
      this.planName = t('zoom_one_edu_bundle.school.title')
      const props = pick(this, ['largeMeetingCallback', 'enableDisplayZoomWorkFlow', 'enableDisplayZoomDrive', 'enableDocsFeature', 'enableDisplayZoomTasksWithoutAccountCheck', 'showCampusScheduler', 'isEligibleAccount4CustomAvatarFeature', 'enableLargeMeeting3k5k', 'enableEduWorkspaceReservations'])
      const extraData = { recordingCapacity: RECORDING_CAPACITY.SCHOOL };
      const payLoad = { ...props, ...extraData }
      this.detailItems = zoomOneEduSchoolPlanDetailsItemsGetter(payLoad)
    },
    handleSchoolPlus() {
      this.planName = t('zoom_one_edu_bundle.school_plus.title')
      const props = pick(this, ['largeMeetingCallback', 'enableDisplayZoomWorkFlow', 'enableDisplayZoomDrive', 'enableDocsFeature', 'enableDisplayZoomTasksWithoutAccountCheck', 'isEligibleAccount4CustomAvatarFeature', 'enableLargeMeeting3k5k', 'enableEduWorkspaceReservations'])
      const extraData = { recordingCapacity: RECORDING_CAPACITY.SCHOOL_PLUS };
      const payLoad = { ...props, ...extraData }
      this.detailItems = zoomOneEduSchoolPlusPlanDetailsItemsGetter(payLoad)
    }
  },
  mounted() {
    this.init()
  },

}
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.fbc-inline-block-item {
  display: inline-block;
}
#bp-zoom-one-edu-plan-detail {
  border-radius: 12px;
  padding: 60px;
  min-width: 550px;
  width: min-content;
}
.bp-zone-edu-detail-container {
  &__plan-title {
    font-weight: 700;
    font-size: 22px;
    color: #515079;
  }
  .bp-zone-edu-detail-price {
    margin-top: 27px;
    display: flex;
    align-items: center;
    column-gap: 24px;
    height: fit-content;
    &__divider {
      width: 1px;
      height: 58px;
      background-color: #E0E0E6;
    }
    .bp-zone-edu-price {
      display: flex;
      &__integer {
        margin-left: 3px;
        margin-right: 2px;
        font-weight: 600;
        font-size: 32px;
        line-height: 38.4px;
        color: #00053d;
      }
      &__decimal {
        font-weight: 500;
        font-size: 14px;
        line-height: 21px;
        color: #00053d;
      }
      &__unit {
        display: inline-flex;
        flex-direction: column;
        align-self: flex-end;
        font-weight: 500;
        font-size: 16px;
        line-height: 24px;
        color: #00053d;
        .dot {
          display: none;
        }
      }
    }
  }
  .bp-zone-edu-detail-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 15px;
    &__feature-list {
      margin-top: 33px;
      color: #0b5cff;
      font-weight: 500;
      font-size: 14px;
      line-height: 21px;
    }
    .bp-zone-edu-detail-item-part {
      display: flex;
      align-items: baseline;
      .zone-edu-check {
        width: 12px;
        height: 12px;
      }
      .bp-zone-edu-detail-content {
        &__icon {
          margin-left: 4px;
          width: 14px;
          height: 14px;
        }
        &__title {
          margin-left: 4px;
          font-weight: 600;
          font-size: 14px;
          line-height: 21px;
          color: #00053d;
        }
        &__desc {
          margin-left: 3px;
          font-weight: 450;
          font-size: 14px;
          line-height: 21px;
          color: #666487;
        }
        &__tip {
          display: flex;
          align-items: flex-start;
          font-weight: 450;
          font-size: 14px;
          line-height: 21px;
          color: #0b5cff;
          cursor: pointer;
        }
        &__feature-list {
          padding-left: 14px;
          ul {
            list-style-type: disc;
          }
        }
      }
    }
  }
}

@include media(mobile) {
  #bp-zoom-one-edu-plan-detail {
    border-radius: 12px;
    padding: 60px;
  }
  .bp-zone-edu-detail-container {
    &__plan-title {
      font-size: 22px;
    }
    .bp-zone-edu-detail-price {
      margin-top: 16px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      &__divider {
        display: none;
      }
      .bp-zone-edu-price {
        &__integer {
          font-size: 20px;
          line-height: 24px;
        }
        &__decimal {
          font-size: 12px;
          line-height: 18px;
        }
        &__unit {
          font-size: 14px;
          line-height: 18px;
        }
      }
    }
    .bp-zone-edu-detail-items {
      gap: 12px;
      margin-top: 16px;
      &__feature-list {
        margin-top: 16px;
        font-size: 14px;
        line-height: 18px;
      }
      .bp-zone-edu-detail-item-part {
        .zone-edu-check {
          width: 10px;
          height: 10px;
        }
        .bp-zone-edu-detail-content {
          &__icon {
            margin-left: 4px;
            width: 14px;
            height: 14px;
          }
          &__title {
            margin-left: 4px;
            font-weight: 600;
            font-size: 14px;
            line-height: 22px;
          }
          &__desc {
            margin-left: 3px;
            font-weight: 450;
            font-size: 12px;
            line-height: 18px;
          }
          &__tip {
            font-weight: 450;
            font-size: 12px;
            line-height: 18px;
          }
        }
      }
    }
  }
}
</style>
